import { useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@humansignal/ui";
import { AuthShell, CardThemeToggle } from "./AuthShell";
import { NetIndicator } from "./NetIndicator";
import { OtherWays } from "./OtherWays";
import { setTokens, apiUrl } from "../../utils/jwt-storage";
import { safeNextPath, spaNavigate } from "../../utils/navigation";
import { refreshAuthHeaders } from "../../providers/ApiProvider";
import { useBranding } from "../../providers/BrandingProvider";

/**
 * React port of users/templates/users/new-ui/user_signup.html.
 *
 * Posts ``{email, password}`` to the JSON
 * sign-up endpoint at ``/api/auth/signup/`` (see
 * ``users/api_auth.py``). On success, stashes the access + refresh
 * JWTs in localStorage and redirects to ``/``.
 */
export const Signup = () => {
  const initialEmail = (() => {
    // ``?email=…`` carried forward from the Login step-1 "not found"
    // branch — user typed their email, got "no such account", and got
    // routed here with the email pre-filled so they don't retype.
    try {
      return new URLSearchParams(window.location.search).get("email") || "";
    } catch {
      return "";
    }
  })();
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();
  // Account-type choice + consent (offline-account plan, Phase 1+):
  //   - Desktop: the user EXPLICITLY chooses device-only vs cloud —
  //     online or offline, never a silent default.
  //   - Every surface: creating a CLOUD account requires ticking the
  //     consent box (email, name, and shared-project work are stored
  //     on AfriAnnotate's servers).
  const isDesktopShell =
    (window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost") &&
    window.location.port === "17080";
  const [accountType, setAccountType] = useState<"local" | "cloud">(isDesktopShell ? "local" : "cloud");
  const [cloudConsent, setCloudConsent] = useState(false);
  // Privacy notice link shown with the consent line. Operator-set
  // (Platform → Settings → External links, or PLATFORM_PRIVACY_URL).
  const branding = useBranding() as { privacy_url?: string } | null;
  const privacyUrl = branding?.privacy_url || "";
  const needsConsent = accountType === "cloud";
  // Desktop + cloud unreachable → the cloud option is disabled and
  // device-only is the only path. Probing the LOCAL server's
  // cloud_reachable endpoint — authoritative on the desktop, where
  // /api/version/ is the always-up local Django.
  const [desktopOffline, setDesktopOffline] = useState(false);
  useEffect(() => {
    if (!isDesktopShell) return undefined;
    let alive = true;
    const probe = async () => {
      try {
        const r = await fetch("/api/afri/auth/cloud_reachable/", { cache: "no-store" });
        const b = (await r.json()) as { reachable?: boolean };
        if (alive) setDesktopOffline(!b?.reachable);
      } catch {
        if (alive) setDesktopOffline(true);
      }
    };
    probe();
    const id = window.setInterval(probe, 12000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, []);
  // If the connection drops while "cloud" is selected, fall back to
  // device-only — the cloud option is disabled in that state anyway.
  useEffect(() => {
    if (desktopOffline && accountType === "cloud") setAccountType("local");
  }, [desktopOffline, accountType]);

  // Auth.css sets ``form input { width: 100%; padding: 10px 15px }``
  // for the text fields — radios/checkboxes inherit it and render as
  // full-width padded bars, shoving their label text out of the card.
  // Hard-override the control geometry inline.
  const choiceInputStyle: CSSProperties = {
    width: 16,
    height: 16,
    padding: 0,
    margin: "2px 0 0",
    flex: "0 0 auto",
  };
  const choiceTextStyle: CSSProperties = {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    lineHeight: 1.45,
    fontWeight: 400,
  };
  const choiceRowStyle = (dimmed: boolean): CSSProperties => ({
    display: "flex",
    gap: 8,
    alignItems: "flex-start",
    fontWeight: 400,
    marginTop: 6,
    opacity: dimmed ? 0.5 : 1,
  });
  const toast = useToast();
  // Sent here by another way of signing in that found no account for the
  // person (``?why=no_account``): said once.
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get("why") === "no_account") {
        toast?.show({
          message: "There is no account for this address yet. Tick the consent box, then choose how to create one.",
        });
      }
    } catch {
      /* no address to read */
    }
  }, []);
  // Surface ?revoked=1 from VerifyEmailRequired's poller (fires when
  // an account is deleted / disabled while the wait screen was open).
  // The wait-screen redirect points here (not to /auth/login) because
  // "let me try signing up again" is the most likely next action for
  // a just-deleted just-signed-up account. Strip the flag on read so
  // a reload doesn't re-fire the toast.
  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get("revoked") === "1") {
        q.delete("revoked");
        const search = q.toString();
        const nextUrl = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`;
        window.history.replaceState({}, "", nextUrl);
        toast?.show({
          message: "That account is no longer available. Sign up again below, or use a different email.",
          type: "info",
          duration: 8000,
        });
      }
    } catch {
      /* private browsing / SSR */
    }
    // toast.show is stable across renders and we only want this on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    // Client-side confirm match — fail fast before hitting the
    // network. Server doesn't see ``password_confirm``; only the
    // first password field gets persisted.
    if (password !== passwordConfirm) {
      setError("Passwords don't match.");
      return;
    }
    if (needsConsent && !cloudConsent) {
      setError("Please tick the consent box to create a cloud account, or choose a device-only account.");
      return;
    }
    if (isDesktopShell && accountType === "cloud" && desktopOffline) {
      setError(
        "Creating a cloud account needs a connection. Choose a device-only account, or reconnect and try again.",
      );
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(apiUrl("/api/auth/signup/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          password,
          ...(isDesktopShell ? { account_type: accountType } : {}),
          cloud_consent: needsConsent ? cloudConsent : false,
          // Include the survey answer so it lands in the user's
          // profile like the Django form path did. Sent even when
          // blank so the server sees a consistent shape.
          // ``token`` (signup-link) is included if the URL ?token=
          // query string carries one — mirrors the Django form's
          // hidden field.
          token: new URLSearchParams(window.location.search).get("token") || "",
        }),
      });
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (!res.ok) {
        const detail = body.error ?? "Sign-up failed.";
        setError(typeof detail === "string" ? detail : "Sign-up failed.");
        return;
      }
      const access = body.access as string | undefined;
      const refresh = body.refresh as string | undefined;
      const user = body.user as { is_email_verified?: boolean } | undefined;
      if (access) {
        setTokens(access, refresh ?? null);
        refreshAuthHeaders();
        await queryClient.invalidateQueries();
      }
      // Route based on server verification state: unverified users go
      // to the "check your inbox" wait screen; verified (first user,
      // or a signup-link path that auto-verified) go straight into the
      // app. Matches Django's ``user_signup`` view flow.
      if (user && user.is_email_verified === false) {
        spaNavigate("/auth/verify-email-required", true);
      } else {
        // Honour ``?next=`` (threaded through from the login page's
        // not-found → signup handoff) so deep links like a facilitator
        // invite QR (``/discover?invite=<token>``) survive signup.
        const next = new URLSearchParams(window.location.search).get("next");
        spaNavigate(safeNextPath(next), true);
      }
    } catch {
      setError("Couldn't reach the server. Check your connection.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <div className="form-wrapper">
        <div className="form-wrapper-header">
          <h2>Sign Up</h2>
          <CardThemeToggle />
          <NetIndicator />
        </div>
        <form id="signup-form" onSubmit={onSubmit}>
          <div className="input-wrapper">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              inputMode="email"
              className="lsf-input-ls"
              name="email"
              id="email"
              autoComplete="email"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="input-wrapper">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              className="lsf-input-ls"
              name="password"
              id="password"
              autoComplete="new-password"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="input-wrapper">
            <label htmlFor="password_confirm">Confirm password</label>
            <input
              type="password"
              className="lsf-input-ls"
              name="password_confirm"
              id="password_confirm"
              autoComplete="new-password"
              minLength={8}
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
            />
          </div>
          {isDesktopShell && (
            <div className="input-wrapper" role="radiogroup" aria-label="Account type">
              <label>Account type</label>
              <label style={choiceRowStyle(false)}>
                <input
                  type="radio"
                  name="account_type"
                  checked={accountType === "local"}
                  onChange={() => setAccountType("local")}
                  style={choiceInputStyle}
                />
                <span style={choiceTextStyle}>
                  <strong>Device-only</strong>: everything stays on this computer; works fully offline. You can link it
                  to the cloud later, with your explicit consent.
                </span>
              </label>
              <label style={choiceRowStyle(desktopOffline)}>
                <input
                  type="radio"
                  name="account_type"
                  checked={accountType === "cloud"}
                  disabled={desktopOffline}
                  onChange={() => setAccountType("cloud")}
                  style={choiceInputStyle}
                />
                <span style={choiceTextStyle}>
                  <strong>Cloud account</strong>: sync and collaborate across devices
                  {desktopOffline ? " (requires a connection, you're offline)" : ""}.
                </span>
              </label>
            </div>
          )}
          {needsConsent && (
            <label className="input-wrapper" style={choiceRowStyle(false)}>
              <input
                type="checkbox"
                checked={cloudConsent}
                onChange={(e) => setCloudConsent(e.target.checked)}
                style={choiceInputStyle}
              />
              <span style={choiceTextStyle}>
                I consent to AfriAnnotate storing my email address, name, and my annotation work on shared projects on
                its servers, so the tool can work and my contributions can reach the projects I join.
                {privacyUrl && (
                  <>
                    {" "}
                    <a href={privacyUrl} target="_blank" rel="noreferrer">
                      Read the privacy notice
                    </a>
                    .
                  </>
                )}
              </span>
            </label>
          )}
          {error && <p className="error">{error}</p>}
          <button
            type="submit"
            aria-label="Create Account"
            className="lsf-button-ls lsf-button-ls_look_primary"
            disabled={submitting || (needsConsent && !cloudConsent)}
          >
            {submitting
              ? "Creating account…"
              : isDesktopShell && accountType === "local"
                ? "Create device-only account"
                : "Create Account"}
          </button>
        </form>
        {!(isDesktopShell && accountType === "local") && (
          <OtherWays
            query={{
              intent: "signup",
              consent: cloudConsent ? "1" : "",
              token: new URLSearchParams(window.location.search).get("token") || "",
            }}
            refuse={() => {
              if (!needsConsent || cloudConsent) return false;
              setError("Please tick the consent box first.");
              return true;
            }}
          />
        )}
      </div>
      <div className="text-wrapper">
        <p>Already have an account?</p>
        {/* SPA-nav — see the same-shape comment in Login.tsx for why
            a plain ``href`` full-navs to a non-existent local path on
            Capacitor. */}
        <a
          href="/auth/login"
          onClick={(e) => {
            e.preventDefault();
            spaNavigate("/auth/login", true);
          }}
        >
          Log in
        </a>
      </div>
    </AuthShell>
  );
};

export default Signup;
